"use client"

import { useState, useEffect } from "react"
import Link from "next/link"
import type { LeaderboardData } from "@/types/leaderboard"
import { track } from "@vercel/analytics"
import { ChevronLeft, ChevronRight, Trophy, Loader2, ArrowUp, ArrowDown } from "lucide-react"
import LeaderboardAvatar from "@/components/leaderboard-avatar"
import { motion } from "framer-motion"

interface LeaderboardClientProps {
  initialData: LeaderboardData
}

export default function LeaderboardClient({ initialData }: LeaderboardClientProps) {
  const [entriesPerPage] = useState(100)
  const [currentPage, setCurrentPage] = useState(1)
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardData>(initialData)
  const [isLoading, setIsLoading] = useState(false)
  const [isVisible, setIsVisible] = useState(false)

  // Force animations to run on mount
  useEffect(() => {
    setIsVisible(true)
  }, [])

  // Track page view when component mounts
  useEffect(() => {
    track("view_leaderboard")
  }, [])

  // Calculate pagination
  const totalEntries = leaderboardData.totalEntries
  const totalPages = Math.max(1, Math.ceil(totalEntries / entriesPerPage))

  // Fetch leaderboard data for the current page
  const fetchLeaderboardPage = async (page: number) => {
    try {
      setIsLoading(true)
      const response = await fetch(`/api/leaderboard?page=${page}&limit=${entriesPerPage}`)
      if (!response.ok) {
        throw new Error(`Error fetching leaderboard: ${response.status}`)
      }
      const data = await response.json()
      setLeaderboardData(data)
    } catch (error) {
      console.error("Error fetching leaderboard page:", error)
    } finally {
      setIsLoading(false)
    }
  }

  // Format date
  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  // Format wallet address
  const formatAddress = (address: string) => {
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`
  }

  // Handle pagination
  const handlePageChange = (pageNumber: number) => {
    track("leaderboard_page_change", { page: pageNumber })
    setCurrentPage(pageNumber)
    fetchLeaderboardPage(pageNumber)
    // Scroll to top of table
    document.querySelector(".overflow-hidden.rounded-xl")?.scrollIntoView({ behavior: "smooth" })
  }

  // Track when a user clicks on a wallet address
  const handleWalletClick = (walletAddress: string, rank: number) => {
    track("leaderboard_wallet_click", {
      wallet: walletAddress.substring(0, 6),
      rank,
    })
  }

  // Check if change is recent (within the last 24 hours)
  const isRecentChange = (timestamp?: number) => {
    if (!timestamp) return false
    const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000
    return timestamp > oneDayAgo
  }

  // Format change numbers for better display on mobile
  const formatChangeNumber = (change: number) => {
    if (Math.abs(change) >= 1000) {
      return `${change > 0 ? "+" : ""}${(change / 1000).toFixed(1)}k`
    }
    return `${change > 0 ? "+" : ""}${change}`
  }

  return (
    <div className="w-full">
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Rank
                </th>
                <th
                  scope="col"
                  className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Collector
                </th>
                <th
                  scope="col"
                  className="px-2 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Blocks
                </th>
                <th
                  scope="col"
                  className="px-2 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  NFTs
                </th>
                <th
                  scope="col"
                  className="px-2 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell"
                >
                  Updated
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center">
                    <div className="flex justify-center items-center">
                      <Loader2 className="h-6 w-6 animate-spin text-brand-purple mr-2" />
                      <p className="text-gray-500">Loading collectors...</p>
                    </div>
                  </td>
                </tr>
              ) : leaderboardData.entries.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center">
                    <p className="text-gray-500">No entries found in the leaderboard yet.</p>
                    <p className="text-gray-400 text-sm mt-2">Be the first to check your wallet and appear here!</p>
                  </td>
                </tr>
              ) : (
                leaderboardData.entries.map((entry, index) => {
                  const rank = (currentPage - 1) * entriesPerPage + index + 1
                  const hasCollectionChange =
                    entry.collectionCountChange !== undefined && entry.collectionCountChange !== 0
                  const hasTokenChange = entry.tokenCountChange !== undefined && entry.tokenCountChange !== 0
                  const showCollectionChange = hasCollectionChange && isRecentChange(entry.changeTimestamp)
                  const showTokenChange = hasTokenChange && isRecentChange(entry.changeTimestamp)

                  return (
                    <tr key={entry.walletAddress} className="hover:bg-gray-50 transition-colors">
                      <td className="px-2 sm:px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {rank <= 3 ? (
                            <div
                              className={`
                                flex items-center justify-center w-8 h-8 rounded-full 
                                ${
                                  rank === 1
                                    ? "bg-yellow-100 text-yellow-600"
                                    : rank === 2
                                      ? "bg-gray-100 text-gray-600"
                                      : "bg-amber-100 text-amber-600"
                                }
                              `}
                            >
                              <Trophy size={16} />
                            </div>
                          ) : (
                            <div className="flex items-center justify-center w-8 sm:justify-start sm:w-auto">
                              <span className="text-sm font-medium text-gray-900">{rank}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-2 sm:px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-3">
                          <LeaderboardAvatar address={entry.walletAddress} ensName={entry.ensName} />
                          <Link
                            href={`/results?wallet=${entry.walletAddress}`}
                            onClick={() => handleWalletClick(entry.walletAddress, rank)}
                            className="text-brand-purple text-sm hover:text-brand-purple/80 transition-colors font-medium"
                          >
                            {entry.ensName || formatAddress(entry.walletAddress)}
                          </Link>
                        </div>
                      </td>
                      <td className="px-2 sm:px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm font-medium text-gray-900 flex items-center justify-end">
                          <span>{entry.collectionCount}</span>
                          {showCollectionChange && (
                            <span
                              className={`ml-1 text-xs px-1.5 py-0.5 rounded-full flex items-center whitespace-nowrap
                                ${entry.collectionCountChange! > 0 ? "text-green-600 bg-green-50 border border-green-100" : "text-red-600 bg-red-50 border border-red-100"}
                                sm:inline-flex sm:ml-1`}
                              title={`Changed from ${entry.previousCollectionCount} on ${formatDate(entry.changeTimestamp!)}`}
                            >
                              <span>{formatChangeNumber(entry.collectionCountChange!)}</span>
                              {entry.collectionCountChange! > 0 ? (
                                <ArrowUp size={12} strokeWidth={4.5} className="ml-0.5 stroke-[1.5]" />
                              ) : (
                                <ArrowDown size={12} strokeWidth={4.5} className="ml-0.5 stroke-[1.5]" />
                              )}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-2 sm:px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm text-gray-900 flex items-center justify-end">
                          <span>{entry.tokenCount}</span>
                          {showTokenChange && (
                            <span
                              className={`ml-1 text-xs px-1.5 py-0.5 rounded-full flex items-center whitespace-nowrap
                                ${entry.tokenCountChange! > 0 ? "text-green-600 bg-green-50 border border-green-100" : "text-red-600 bg-red-50 border border-red-100"}
                                sm:inline-flex sm:ml-1`}
                              title={`Changed from ${entry.previousTokenCount} on ${formatDate(entry.changeTimestamp!)}`}
                            >
                              <span>{formatChangeNumber(entry.tokenCountChange!)}</span>
                              {entry.tokenCountChange! > 0 ? (
                                <ArrowUp size={12} strokeWidth={4.5} className="ml-0.5 stroke-[1.5]" />
                              ) : (
                                <ArrowDown size={12} strokeWidth={4.5} className="ml-0.5 stroke-[1.5]" />
                              )}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-2 sm:px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500 hidden sm:table-cell">
                        {formatDate(entry.lastUpdated)}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex flex-wrap justify-center mt-6 gap-2 w-full">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1 || isLoading}
          className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm w-28"
        >
          <ChevronLeft size={16} className="mr-1" />
          Previous
        </button>

        <div className="flex flex-wrap gap-2">
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter((page) => {
              // Show first page, last page, current page, and pages around current page
              return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1
            })
            .map((page, index, array) => {
              // Add ellipsis if there are gaps
              const showEllipsisBefore = index > 0 && array[index - 1] !== page - 1
              const showEllipsisAfter = index < array.length - 1 && array[index + 1] !== page + 1

              return (
                <div key={page} className="flex items-center">
                  {showEllipsisBefore && <span className="px-3 py-2 text-gray-500">...</span>}

                  <button
                    onClick={() => handlePageChange(page)}
                    disabled={isLoading}
                    className={`px-3 py-2 border rounded-md w-10 flex items-center justify-center ${
                      currentPage === page
                        ? "bg-brand-purple text-white border-brand-purple"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                    } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {page}
                  </button>

                  {showEllipsisAfter && <span className="px-3 py-2 text-gray-500">...</span>}
                </div>
              )
            })}
        </div>

        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages || isLoading}
          className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm w-28"
        >
          Next
          <ChevronRight size={16} className="ml-1" />
        </button>
      </div>

      <div className="mt-8 text-center text-sm text-gray-500">
        <p>Last updated: {formatDate(leaderboardData.lastUpdated || Date.now())}</p>
        <p className="mt-2 text-xs text-gray-400">
          Showing {(currentPage - 1) * entriesPerPage + 1}-
          {Math.min(currentPage * entriesPerPage, leaderboardData.totalEntries)} of {leaderboardData.totalEntries}{" "}
          entries
        </p>
      </div>
    </div>
  )
}
